import type { ZoneStatisticsSummary } from '../types/zone';
import { puDeviation } from './electricalConstants';
import { parseUtcTimestampMs } from './timeUtils';
import { loadingSeverity } from './loadingConstants';
import {
  VOLTAGE_OPERATIONAL_MIN_PU, VOLTAGE_OPERATIONAL_MAX_PU,
  VOLTAGE_WARNING_LOW_PU, VOLTAGE_WARNING_HIGH_PU,
} from './voltageConstants';

/**
 * Health, sorting and filtering of the dashboard zone cards. Pure functions — the landing page
 * and the dashboard store share them so the "148 Zonen" list can be ordered by what matters
 * to the operator (favourites, status, loading, alerts) instead of the backend's zone order.
 */

export type ZoneHealth = 'green' | 'yellow' | 'red' | 'gray' | 'archive';

export const ZONE_HEALTH_ORDER: ZoneHealth[] = ['red', 'yellow', 'green', 'gray', 'archive'];

export const ZONE_HEALTH_LABELS: Record<ZoneHealth, string> = {
  green: 'Normal', yellow: 'Warnung', red: 'Kritisch', gray: 'Keine Daten', archive: 'Archiv',
};

/** Inactive zone fed from the snapshot archive — no live values, but a full history. */
export function isArchiveZone(s: ZoneStatisticsSummary): boolean {
  return !s.isActive && s.archiveTo != null && (s.archiveSnapshotCount ?? 0) > 0;
}

export function getHealthStatus(s: ZoneStatisticsSummary): ZoneHealth {
  if (isArchiveZone(s)) return 'archive';
  if (!s.lastSnapshotTimestamp) return 'gray';
  const cable = s.currentMaxCableLoadingPercent ?? s.maxCableLoadingPercent24h ?? null;
  const trafo = s.currentMaxTrafoLoadingPercent ?? s.maxTrafoLoadingPercent24h ?? null;
  const minV = s.currentMinVoltagePu ?? s.minVoltagePu24h ?? null;
  const maxV = s.currentMaxVoltagePu ?? s.maxVoltagePu24h ?? null;

  // If no power flow data available yet, show gray
  if (cable == null && trafo == null && minV == null && maxV == null) return 'gray';

  // Product Ampel: loading above the legal cap / voltage outside the EN 50160 band = red,
  // loading above the warning threshold / voltage inside the warning margin = yellow.
  // Cable AND transformer: the transformer was missing here until 2026-09, an overloaded
  // station left its zone green.
  const severities = [loadingSeverity(cable), loadingSeverity(trafo)];
  const loading = severities.includes('critical') ? 'critical' : severities.includes('warning') ? 'warning' : 'normal';
  if (loading === 'critical' || (minV != null && minV < VOLTAGE_OPERATIONAL_MIN_PU) || (maxV != null && maxV > VOLTAGE_OPERATIONAL_MAX_PU)) return 'red';
  if (loading === 'warning' || (minV != null && minV < VOLTAGE_WARNING_LOW_PU) || (maxV != null && maxV > VOLTAGE_WARNING_HIGH_PU)) return 'yellow';
  return 'green';
}

export type ZoneSortKey =
  | 'status'
  | 'name'
  | 'cableLoading'
  | 'trafoLoading'
  | 'voltage'
  | 'alerts'
  | 'houses'
  | 'updated';

export const ZONE_SORT_OPTIONS: { key: ZoneSortKey; label: string }[] = [
  { key: 'status', label: 'Status (kritisch zuerst)' },
  { key: 'name', label: 'Name (A–Z)' },
  { key: 'cableLoading', label: 'Kabelauslastung (höchste zuerst)' },
  { key: 'trafoLoading', label: 'Trafoauslastung (höchste zuerst)' },
  { key: 'voltage', label: 'Spannungsabweichung (größte zuerst)' },
  { key: 'alerts', label: 'Alarme 24h (meiste zuerst)' },
  { key: 'houses', label: 'Hausanschlüsse (meiste zuerst)' },
  { key: 'updated', label: 'Zuletzt aktualisiert' },
];

/** Live value first, then the 24h extreme, then the archive extreme — whichever the zone has. */
export function maxCableLoading(s: ZoneStatisticsSummary): number | null {
  return s.currentMaxCableLoadingPercent ?? s.maxCableLoadingPercent24h ?? s.archiveMaxCableLoadingPercent ?? null;
}

export function maxTrafoLoading(s: ZoneStatisticsSummary): number | null {
  return s.currentMaxTrafoLoadingPercent ?? s.maxTrafoLoadingPercent24h ?? s.archiveMaxTrafoLoadingPercent ?? null;
}

/** Largest |deviation from Un| in pu over both band ends (live, else 24h, else archive). */
export function worstVoltageDeviation(s: ZoneStatisticsSummary): number | null {
  const minV = s.currentMinVoltagePu ?? s.minVoltagePu24h ?? s.archiveMinVoltagePu ?? null;
  const maxV = s.currentMaxVoltagePu ?? s.maxVoltagePu24h ?? s.archiveMaxVoltagePu ?? null;
  const devs = [minV, maxV].filter((v): v is number => v != null && v > 0).map(v => Math.abs(puDeviation(v)));
  return devs.length > 0 ? Math.max(...devs) : null;
}

export function alertCount24h(s: ZoneStatisticsSummary): number {
  return (s.trafoAlertCount24h ?? 0) + (s.voltageAlertCount24h ?? 0) + (s.cableAlertCount24h ?? 0);
}

function lastUpdateMs(s: ZoneStatisticsSummary): number | null {
  const ts = s.lastPowerFlowTimestamp ?? s.lastSnapshotTimestamp ?? s.archiveTo ?? null;
  if (!ts) return null;
  const ms = parseUtcTimestampMs(ts);
  return Number.isNaN(ms) ? null : ms;
}

function byName(a: ZoneStatisticsSummary, b: ZoneStatisticsSummary): number {
  return a.zoneName.localeCompare(b.zoneName, 'de');
}

/** Descending on a nullable number, nulls last; ties fall back to the name. */
function descNullsLast(pick: (s: ZoneStatisticsSummary) => number | null) {
  return (a: ZoneStatisticsSummary, b: ZoneStatisticsSummary): number => {
    const va = pick(a);
    const vb = pick(b);
    if (va == null && vb == null) return byName(a, b);
    if (va == null) return 1;
    if (vb == null) return -1;
    return vb - va || byName(a, b);
  };
}

const comparators: Record<ZoneSortKey, (a: ZoneStatisticsSummary, b: ZoneStatisticsSummary) => number> = {
  status: (a, b) =>
    ZONE_HEALTH_ORDER.indexOf(getHealthStatus(a)) - ZONE_HEALTH_ORDER.indexOf(getHealthStatus(b))
    || alertCount24h(b) - alertCount24h(a)
    || byName(a, b),
  name: byName,
  cableLoading: descNullsLast(maxCableLoading),
  trafoLoading: descNullsLast(maxTrafoLoading),
  voltage: descNullsLast(worstVoltageDeviation),
  alerts: descNullsLast(s => alertCount24h(s)),
  houses: descNullsLast(s => s.houseConnectionCount),
  updated: descNullsLast(lastUpdateMs),
};

/** Favourites always lead; within each group the chosen sort applies. Returns a new array. */
export function sortZoneSummaries(
  list: ZoneStatisticsSummary[],
  sortKey: ZoneSortKey,
  favoriteZoneIds: ReadonlySet<string>
): ZoneStatisticsSummary[] {
  const cmp = comparators[sortKey];
  return [...list].sort((a, b) => {
    const fa = favoriteZoneIds.has(a.zoneId) ? 0 : 1;
    const fb = favoriteZoneIds.has(b.zoneId) ? 0 : 1;
    return fa - fb || cmp(a, b);
  });
}

export interface ZoneCardFilters {
  /** Empty = every status. */
  statuses: ReadonlySet<ZoneHealth>;
  onlyFavorites: boolean;
  onlyWithAlerts: boolean;
}

export function matchesZoneCardFilters(
  s: ZoneStatisticsSummary,
  filters: ZoneCardFilters,
  favoriteZoneIds: ReadonlySet<string>
): boolean {
  if (filters.onlyFavorites && !favoriteZoneIds.has(s.zoneId)) return false;
  if (filters.onlyWithAlerts && alertCount24h(s) === 0) return false;
  if (filters.statuses.size > 0 && !filters.statuses.has(getHealthStatus(s))) return false;
  return true;
}
